import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  /** Exige digitar este texto para liberar o botão (ações irreversíveis grandes). */
  typeToConfirm?: string;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  loading = false,
  ...rest
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" busy={loading}>
        {/* Corpo separado: desmonta ao fechar, então o texto digitado zera sozinho. */}
        <ConfirmBody {...rest} loading={loading} onCancel={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function ConfirmBody({
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  loading,
  typeToConfirm,
  onConfirm,
  onCancel,
}: Omit<ConfirmDialogProps, 'open' | 'onOpenChange'> & { onCancel: () => void }) {
  const [typed, setTyped] = useState('');
  const blocked = Boolean(typeToConfirm) && typed.trim() !== typeToConfirm;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      {typeToConfirm && (
        <div className="mt-4 space-y-2">
          <label htmlFor="confirm-dialog-type" className="text-sm">
            Para confirmar, digite <strong className="font-semibold">{typeToConfirm}</strong>
          </label>
          <Input
            id="confirm-dialog-type"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            autoFocus
          />
        </div>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" disabled={loading} onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          type="button"
          variant={destructive ? 'destructive' : 'default'}
          disabled={loading || blocked}
          onClick={onConfirm}
        >
          {loading && <Loader2 className="animate-spin" />}
          {confirmLabel}
        </Button>
      </DialogFooter>
    </>
  );
}

type ConfirmOptions = Omit<ConfirmDialogProps, 'open' | 'onOpenChange' | 'onConfirm' | 'loading'>;
type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Substitui `window.confirm`: `if (!(await confirm({ title, destructive: true }))) return;` */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  // As opções ficam guardadas após fechar para o texto não sumir durante a animação de saída.
  const [options, setOptions] = useState<ConfirmOptions>({ title: '' });
  const [open, setOpen] = useState(false);
  const resolver = useRef<((value: boolean) => void) | undefined>(undefined);

  const confirm = useCallback<ConfirmFn>((opts) => {
    resolver.current?.(false);
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = undefined;
    setOpen(false);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        {...options}
        open={open}
        onOpenChange={(open) => !open && settle(false)}
        onConfirm={() => settle(true)}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm precisa estar dentro de <ConfirmProvider>');
  return confirm;
}

/**
 * Envolve o `onOpenChange` de um formulário: fechar (Esc, ✕, Cancelar) com alterações
 * pergunta "Descartar alterações?" antes. Uso: `<Dialog onOpenChange={useDiscardGuard(isDirty, setOpen)}>`.
 */
export function useDiscardGuard(isDirty: boolean, onOpenChange: (open: boolean) => void) {
  const confirm = useConfirm();
  return useCallback(
    async (open: boolean) => {
      if (open || !isDirty) return onOpenChange(open);
      const discard = await confirm({
        title: 'Descartar alterações?',
        description: 'O que você preencheu será perdido.',
        confirmLabel: 'Descartar',
        cancelLabel: 'Continuar editando',
        destructive: true,
      });
      if (discard) onOpenChange(false);
    },
    [confirm, isDirty, onOpenChange],
  );
}
